<template>
  <v-container class="new-user mx-2" fluid>
    <v-breadcrumbs class="new-user__breadcrumbs px-0 pt-0 pb-2" :items="items" />
    <div class="new-user__header">
      <v-icon color="primary" class="new-user__header-icon">mdi-account-plus</v-icon>
      <h1 class="new-user__title">{{ $route.meta.title }}</h1>
    </div>
    <v-card class="new-user__card" flat outlined>
      <v-card-text class="pa-4">
        <v-form ref="form" v-model="valid" lazy-validation>
          <h2 class="new-user__section-title mb-3">
            <v-icon left small>mdi-information-outline</v-icon>
            Данные пользователя
          </h2>
          <v-row>
            <v-col cols="12" sm="6" class="new-user__field-col">
              <v-text-field
                v-model="name"
                label="Имя"
                outlined
                dense
                flat
                prepend-inner-icon="mdi-account-outline"
                class="new-user__field"
                :rules="[() => !!name || 'Поле обязательно для заполнения']"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" class="new-user__field-col">
              <v-text-field
                v-model="email"
                label="Email"
                outlined
                dense
                flat
                prepend-inner-icon="mdi-email-outline"
                class="new-user__field"
                :rules="[() => /.+@.+\..+/.test(email) || 'E-mail должен быть корректным']"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" class="new-user__field-col">
              <v-text-field
                v-model="telephone"
                label="Телефон"
                type="tel"
                hint="Будет использоваться для тестовых привязок мобильного приложения"
                persistent-hint
                outlined
                dense
                flat
                prepend-inner-icon="mdi-phone-outline"
                class="new-user__field"
                @input="telephone = formatPhoneNumberTextField(telephone || '')"
                :maxlength="telephone.startsWith('+7') ? 16 : null"
              />
            </v-col>
            <v-col cols="12" sm="6" class="new-user__field-col">
              <v-text-field
                v-model="password"
                label="Пароль"
                type="password"
                outlined
                dense
                flat
                prepend-inner-icon="mdi-lock-outline"
                class="new-user__field"
                :rules="[() => password.length > 5 || 'Пароль должен быть не менее 6 символов']"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" class="new-user__field-col">
              <v-text-field
                v-model="passwordConfirmation"
                label="Подтверждение пароля"
                type="password"
                outlined
                dense
                flat
                prepend-inner-icon="mdi-lock-check-outline"
                class="new-user__field"
                :rules="[
                  () => passwordConfirmation.length > 5 || 'Пароль должен быть не менее 6 символов',
                  () => passwordConfirmation === this.password || 'Пароли не совпадают',
                ]"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" class="new-user__field-col">
              <v-autocomplete
                v-model="idRole"
                label="Тип роли"
                :items="typesRole"
                item-text="name"
                item-value="value"
                outlined
                dense
                flat
                hide-details
                clearable
                prepend-inner-icon="mdi-account-key-outline"
                class="new-user__field"
                :rules="[() => !!idRole || 'Поле обязательно для заполнения']"
              />
            </v-col>
            <v-col v-if="roleName !== 'agent'" cols="12" sm="6" class="new-user__field-col">
              <v-autocomplete
                v-model="agentId"
                label="Агент"
                :items="organizations"
                item-text="user.name"
                item-value="id"
                outlined
                dense
                flat
                hide-details
                prepend-inner-icon="mdi-account-tie-outline"
                class="new-user__field"
              />
            </v-col>
            <v-col v-if="roleName !== 'agent' && idRole === 11" cols="12" sm="6" class="new-user__field-col">
              <v-autocomplete
                v-model="ResidentialComplexesIds"
                label="Жилой комплекс"
                :items="resResidentialComplexes"
                item-text="name"
                item-value="id"
                multiple
                outlined
                dense
                flat
                hide-details
                prepend-inner-icon="mdi-home-city-outline"
                class="new-user__field"
                :rules="[() => !!ResidentialComplexesIds || 'Поле обязательно для заполнения']"
              />
            </v-col>
            <v-col v-if="roleName !== 'agent' && idRole === 10" cols="12" sm="6" class="new-user__field-col">
              <v-autocomplete
                v-model="ServiceAreaIds"
                label="Районы обслуживания"
                :items="resServiceAreas"
                item-text="name"
                item-value="id"
                multiple
                outlined
                dense
                flat
                hide-details
                prepend-inner-icon="mdi-map-marker-multiple-outline"
                class="new-user__field"
              />
            </v-col>
          </v-row>
          <div class="new-user__actions">
            <v-btn
              color="primary"
              class="text-none"
              rounded
              :disabled="!valid"
              @click="save()"
            >
              <v-icon left>mdi-content-save-outline</v-icon>
              Сохранить
            </v-btn>
            <v-btn
              color="primary"
              outlined
              rounded
              class="text-none ml-2"
              @click.prevent="returnUsers()"
            >
              <v-icon left>mdi-close</v-icon>
              Отменить
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import formatPhoneNumberTextField from '@/mixins/formatPhoneNumberTextField';

export default {
  name: 'NewUser',
  mixins: [formatPhoneNumberTextField],
  data () {
    return {
      valid: true,
      name: '',
      email: '',
      telephone: '',
      password: '',
      passwordConfirmation: '',
      idRole: 0,
      agentId: '',
      ResidentialComplexesIds: [],
      resResidentialComplexes: [],
      ServiceAreaIds: [],
      resServiceAreas: [],
      typesRole: [
        { name: 'Администратор', value: 1 },
        { name: 'Админ-инженер', value: 5 },
        { name: 'Менаджер', value: 6 },
        { name: 'Аналитик', value: 7 },
        { name: 'Кол-центр', value: 8 },
        { name: 'Мастер', value: 10 },
        { name: 'Администратор КПП', value: 11 },
      ],
      items: [
        { text: 'Пользователи', disabled: false, href: '/users' },
        { text: 'Новый пользователь', disabled: true, href: '#' },
      ],
    };
  },
  async created () {
    const [rcData, serviceAreaData] = await Promise.all([
      this.getResidentialComplex(),
      this.getServiceAreas(),
    ]);
    this.resResidentialComplexes = rcData.data;
    this.resServiceAreas = serviceAreaData.data || [];
  },
  computed: {
    ...mapGetters({
      organizations: 'Agents/agents',
      roleName: 'roleName',
    }),
  },
  methods: {
    ...mapActions({
      newUser: 'User/newUser',
      updateUser: 'User/updateUser',
      loadAllAgents: 'Agents/loadAllAgents',
      getResidentialComplex: 'ResidentComplex/getComplex',
      getServiceAreas: 'ServiceArea/getServiceAreas',
    }),
    returnUsers () {
      this.$router.push({ name: 'Users' });
    },
    save () {
      this.$refs.form.validate();
      this.$nextTick(() => {
        if (this.valid) {
          const telephoneDigits = this.normalizePhoneForSave(this.telephone);
          const data = {
            password: this.password,
            password_confirmation: this.passwordConfirmation,
            name: this.name,
            email: this.email,
            test_phone_activate: telephoneDigits || undefined,
            role_id: this.idRole,
          };
          if (this.agentId) {
            data.agent_id = this.agentId;
          }
          if (this.ResidentialComplexesIds && this.ResidentialComplexesIds.length) {
            data.residential_complex_ids = this.ResidentialComplexesIds;
          }
          if (this.idRole === 10 && this.ServiceAreaIds && this.ServiceAreaIds.length) {
            data.service_area_ids = this.ServiceAreaIds;
          }
          this.newUser(data)
            .then(() => {
              this.returnUsers();
              this.$flash_success('Аккаунт успешно создан');
            })
            .catch((err) => {
              if (err.message.includes('422')) {
                const response = err.response.data;
                for (const input in response) {
                  response[input].forEach(error => this.$flash_warning(input + ': ' + error));
                }
              } else {
                this.$flash_warning('Ошибка при создании пользователя');
              }
            });
        }
      });
    },
    normalizePhoneForSave (phone) {
      const digits = (phone || '').toString().replace(/\D/g, '');
      if (digits.length <= 1) return null;
      return digits;
    },
  },
};
</script>

<style lang="scss" scoped>
.new-user__breadcrumbs {
  margin-bottom: 4px;
}

.new-user__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.new-user__header-icon {
  flex-shrink: 0;
}

.new-user__title {
  font-size: 1.5rem;
  font-weight: 500;
  color: #37474f;
  margin: 0;
  letter-spacing: -0.01em;
}

.new-user__card {
  border-radius: 12px;
  max-width: 720px;
}

.new-user__section-title {
  font-size: 1rem;
  font-weight: 500;
  color: #546e7a;
}

.new-user__field-col {
  padding-top: 8px;
}

.new-user__field ::v-deep .v-input__prepend-inner .v-icon {
  color: rgba(0, 0, 0, 0.5);
}

.new-user__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 32px;
}
</style>
